import { useEffect, useState } from "react";
import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { VersionBadge } from "@/components/VersionBadge";
import { SiteFooter } from "@/components/SiteFooter";
import { installConsoleBuffer } from "@/lib/console-buffer";
import { PostHogTracker } from "@/components/PostHogTracker";

import appCss from "../styles.css?url";

// Absolute URL to a stable, public OG image. iMessage / Slack / Twitter
// require a fully-qualified HTTPS URL and a stable path that won't change
// per build hash, so we serve from /public rather than a hashed asset.
const OG_IMAGE_URL = "https://bracketgoblin.com/og-image.jpg";
const SITE_URL = "https://bracketgoblin.com";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            search={{ redirect: undefined }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#1a1f14" },
      { title: "Bracket Goblin — Brackets Done Right" },
      {
        name: "description",
        content:
          "Run bracket pools with your friends, league, or basement crew. Picks, leaderboards, and zero corporate vibes.",
      },
      { name: "author", content: "Bracket Goblin" },
      { property: "og:site_name", content: "Bracket Goblin" },
      // og:url is intentionally set per-route so social shares deep-link to the
      // shared page rather than the homepage. See each leaf route's head().
      { property: "og:title", content: "Bracket Goblin — Brackets Done Right" },
      {
        property: "og:description",
        content:
          "Run bracket pools with your friends, league, or basement crew. Picks, leaderboards, and zero corporate vibes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE_URL },
      { property: "og:image:secure_url", content: OG_IMAGE_URL },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Bracket Goblin — Brackets Done Right" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Bracket Goblin — Brackets Done Right" },
      {
        name: "twitter:description",
        content:
          "Run bracket pools with your friends, league, or basement crew.",
      },
      { name: "twitter:image", content: OG_IMAGE_URL },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
  }));
  useEffect(() => {
    installConsoleBuffer();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PostHogTracker />
        <div className="flex min-h-screen flex-col">
          <div className="flex-1">
            <Outlet />
          </div>
          <SiteFooter />
        </div>
        <Toaster theme="dark" position="top-center" />
        <VersionBadge />
      </AuthProvider>
    </QueryClientProvider>
  );
}
